// Thin wrapper over the Server Control endpoints.
//
// Every one of these except `restart` raises a StaffActionRequest rather than
// changing anything — the same queue and the same executors the Django admin
// uses, so it does not matter which door an action came through. `restart` is
// the deliberate exception: it acts at once, because a second confirmation
// would defeat restarting a stuck server quickly.

const BASE = '/team-tools/api'

export type ServerHealth = 'live' | 'stale' | 'never-seen'

// Why a server is misbehaving, as opposed to whether it answered. A blocked
// server heartbeats normally, so `health` alone would call it 'live'.
// 'not-landing' is gameapi.services.server_health.Status.NOT_LANDING: up and
// answering, but no join has become a spawn. It had no entry in any map below,
// so the board coloured it `undefined`, left it out of "need a hand" and had
// no chip for it.
export type Diagnosis = 'healthy' | 'idle' | 'degraded' | 'not-landing' | 'blocked' | 'offline' | 'dead'

export type Server = {
    /** Kill races this server counts toward: switched on and not over yet. */
    kill_races?: Array<{ name: string; running: boolean; begins_at: string }>
    id: number
    slot: string | null
    region: string
    host: string
    port: number
    name: string | null
    status: 'active' | 'inactive' | 'destroyed'
    health: ServerHealth
    diagnosis: Diagnosis
    diagnosis_reasons: string[]
    // Whether this server states its own roster on a timer. When true the
    // player count is exact and self-correcting; when false it is an estimate
    // assembled from join reservations, and capacity is not enforced on it.
    reporting: boolean
    roster_age_seconds: number | null
    last_heartbeat_at: string | null
    last_roster_sync_at: string | null
    created_at: string
    destroyed_at: string | null
    fee: number | null
    max_players: number | null
    /** The join password, sent only to those who may edit the server; null for everyone else. */
    password: string | null
    /** Whether a password is set, for everyone. */
    password_set?: boolean
    map_id: number | null
    map_name: string | null
    players_count: number
    // Distance from the requesting client. Always null for staff (the endpoint
    // passes no client ip), but ServerInfo emits it and ServerEdit expects it.
    distance: number | null
    profile: { id: number; name: string } | null
    /** The kind this server was built from, if a deployment made it. */
    built_from?: number | null
    /** The latest recorded spawn-fee change on this server, if any. */
    last_fee_change?: {
        old_fee: string | null
        new_fee: string | null
        at: string
        by: string | null
        source: string
    } | null
    config: Record<string, any>
}

export type ActionRequest = {
    id: number
    kind_label: string
    status: number
    status_label: string
    payload: Record<string, any>
    evidence: Record<string, any>
    error: string
    proposed_at: string
    proposed_by: string | null
    is_actionable: boolean
    kind?: number
    /** Whether this viewer may approve or reject this row. Per row: a grant can cover one kind and not the next. */
    can_decide?: boolean
    /** Your own proposal on a kind that needs a second person (D-048). */
    own_needs_second?: boolean
}

export type ServersResponse = {
    servers: Server[]
    maps: Array<{ id: number; name: string }>
    regions: string[]
    profiles: Array<{ id: number; name: string; region: string }>
}

const csrf = () =>
    document.cookie
        .split('; ')
        .find((c) => c.startsWith('csrftoken='))
        ?.split('=')[1] ?? ''

async function send(path: string, method: string, body?: unknown) {
    const response = await fetch(`${BASE}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: body === undefined ? undefined : JSON.stringify(body),
    })
    let data: any = {}
    try {
        data = await response.json()
    } catch {
        // A 403 from the permission mixin has a body; a 500 may not.
    }
    if (!response.ok) {
        throw new Error(data.error || `${method} ${path} failed (${response.status})`)
    }
    return data
}

export const getServers = (includeDestroyed = false): Promise<ServersResponse> =>
    send(`/servers${includeDestroyed ? '?destroyed=1' : ''}`, 'GET')

export type ServerLoot = {
    pools: Array<{ currency: string; balance: string; reward_min: string; reward_max: string; from_spawn_fee: string }>
    on_map: Array<{ currency: string; count: number; total: string }>
    given: Array<{ currency: string; last_hour: string; last_day: string; all_time: string; pickups: number }>
    recent: Array<{ username: string | null; currency: string; amount: string; at: string }>
    /** Seconds between the game's loot rounds, and drops placed per round. */
    update_frequency: number
    max_spawn_points: number
}

/** Set a server's loot cadence: seconds between rounds, drops per round. */
export const setServerLootConfig = (
    id: number,
    body: { update_frequency?: number; max_spawn_points?: number }
): Promise<{ update_frequency: number; max_spawn_points: number }> => send(`/server/${id}/loot/config`, 'POST', body)

/** Fees BR1 kept off players' deaths on one server, by the victim's spawn currency. */
export type DeathFees = Record<'last_30m' | 'last_24h', { usdc: string; airdrop: string }>
export const getServerDeathFees = (id: number): Promise<DeathFees> => send(`/server/${id}/death-fees`, 'GET')

/** Loot on one server, live. */
export const getServerLoot = (id: number): Promise<ServerLoot> => send(`/server/${id}/loot`, 'GET')

/** Add to a live server's loot pool now; creates the pool if it has none
 *  (then the drop size is needed). */
export const topUpServerLoot = (
    id: number,
    body: { currency: string; amount?: string; reward_min?: string; reward_max?: string }
): Promise<{ currency: string; added: string | null; balance: string; reward_min: string; reward_max: string }> =>
    send(`/server/${id}/loot/top-up`, 'POST', body)

export const getActions = (scope?: 'enforcement' | 'all'): Promise<{ requests: ActionRequest[] }> =>
    send(`/actions${scope ? `?scope=${scope}` : ''}`, 'GET')

export const approveAction = (id: number, note = '') => send(`/actions/${id}/approve`, 'POST', { note })

export const rejectAction = (id: number, note = '') => send(`/actions/${id}/reject`, 'POST', { note })

export const provisionServers = (region: string, count: number) => send('/servers/provision', 'POST', { region, count })

export const applyProfile = (serverId: number) => send(`/server/${serverId}/apply-profile`, 'POST', {})

// The GCP machine always goes with it. Keeping the VM while removing the row
// is what produced orphans — billing, invisible in Team Tools, still holding
// a slot — so it is no longer offered as a choice.
export const destroyServer = (serverId: number) => send(`/server/${serverId}/destroy`, 'POST', {})

/**
 * Tear down every live server, machines included.
 *
 * `expectedCount` is checked against what is actually live and the request is
 * refused if they differ — the dangerous mistake here is not a stray click, it
 * is believing there is one server when there are twenty.
 */
export const destroyAllServers = (expectedCount: number) =>
    send('/servers/destroy-all', 'POST', { expected_count: expectedCount })

// Locations the infrastructure repo's batch workflows accept. Not GCP regions
// and not slots -- the workflow resolves a location to whatever is running
// there, which is why it can reach an orphaned VM that has no server row.
export const BATCH_LOCATIONS = ['us', 'sa', 'eu', 'sea', 'all'] as const

/**
 * Tear down everything running in one location through the GitHub workflow.
 *
 * Different from destroyAllServers above in a way worth keeping straight: that
 * one raises a destroy proposal per GameServer row, this one destroys what GCP
 * actually has running. Use it for a real cleanup, where the machines that need
 * to go may not all have rows.
 */
export const batchDestroyServers = (location: string, expectedCount: number) =>
    send('/servers/batch-destroy', 'POST', {
        location,
        expected_count: expectedCount,
    })

// `refundPlayers` returns the spawn fee to everyone mid-match. Opt-in: a
// restart is also the routine fix for a stuck container, and refunding on every
// bounce would give money away on maintenance.
export const restartServer = (serverId: number, container = 'server', refundPlayers = false) =>
    send(`/server/${serverId}/restart`, 'POST', { container, refund_players: refundPlayers })

export type PlayerAction = {
    type: 'kill' | 'death' | 'spawn' | 'session_end'
    detail: string
    at: string
    seconds_ago: number
}

export type ServerPlayer = {
    user_id: number
    username: string
    joined_at: string
    last_seen_at: string
    seconds_since_seen: number
    // Whether this player has a match actually in progress here. Computed with
    // the same rule the health diagnosis uses, so the roster and the diagnosis
    // cannot describe the same player differently.
    in_match: boolean
    session: { id: string; spawned_at: string; closed_at: string | null; in_progress: boolean } | null
    last_action: PlayerAction | null
}

export const getServerPlayers = (
    serverId: number
): Promise<{ players: ServerPlayer[]; online: number; as_of: string }> => send(`/server/${serverId}/players`, 'GET')

export type FleetServer = {
    id: number
    name: string | null
    slot: string | null
    region: string
    status: string
    diagnosis: Diagnosis
    diagnosis_reasons: string[]
    online: number
    playing: number | null
    max_players: number | null
    reporting: boolean
    roster_age_seconds: number | null
    players: ServerPlayer[]
}

export type FleetPlayers = {
    servers: FleetServer[]
    online_total: number
    reporting_total: number
    as_of: string
}

// One read for the whole fleet. Fanning out over /server/<id>/players from the
// browser would be N round trips whose answers are as-of different instants.
export const getFleetPlayers = (): Promise<FleetPlayers> => send('/players', 'GET')

/**
 * Players over the last few hours, for every live server at once.
 *
 * One read for the whole fleet, so the board can draw a curve on every row
 * without a request per row. The per-server /health call still carries the
 * full series with kills; this is players only, which is all a sparkline
 * shows.
 */
/** Everything a server did, for its life report once it is gone. */
export type ServerLife = {
    players_total: number
    sessions_total: number
    kills_total: number
    fees: { usdc: string; airdrop: string }
    loot_paid: { usdc: string; point: string }
    peak: { players: number; at: string }
    busiest_hour: string
    biggest_drop: { by: number; at: string }
    avg_session_minutes: number
    buckets: Array<{ at: string; players: number }>
}

export const getServerLife = (serverId: number): Promise<ServerLife> => send(`/server/${serverId}/life`, 'GET')

/** Remove a destroyed server's record for good. The endpoint refuses a
 *  server that is not destroyed, so a stale screen cannot purge a live one. */
export const purgeServer = (serverId: number) => send(`/server/${serverId}/purge`, 'POST', {})

/**
 * Ask a server to stop taking joins and exit once the players on it finish.
 *
 * Replaced clearGhostSlots. The leak that button cleaned up cannot happen on a
 * server that reports its roster, and draining is the action that was actually
 * missing: restarting ends every match instantly and leaves paid sessions to
 * refund, while a drain lets them finish.
 *
 * `will_be_honoured` is false when the server is on a build that does not read
 * the instruction -- the screen must say so rather than imply it is winding
 * down.
 */
/**
 * Destroy a dead server's machine and create a fresh one in the same slot.
 *
 * One request, because destroy and create run terraform against the same
 * workspace and must be sequential — the workflow does both in one job. The
 * backend re-checks the DEAD diagnosis and refuses (409) if the server is not
 * dead, so a stale screen cannot destroy a live one.
 */
export const redeployServer = (
    serverId: number,
    body: { machine_type?: string; force?: boolean } = {}
): Promise<{ redeploy: unknown; slot: string | null }> => send(`/server/${serverId}/redeploy`, 'POST', body)

export const drainServer = (
    serverId: number,
    body: { seconds?: number; reason?: string; cancel?: boolean } = {}
): Promise<{
    drain: { seconds: number; reason: string } | null
    will_be_honoured?: boolean
    cancelled?: boolean
}> => send(`/server/${serverId}/drain`, 'POST', body)

/**
 * Take a server out of rotation without destroying it.
 *
 * A database flag, not a Terraform run: it stops appearing in the lobby and
 * stops accepting joins, the machine stays exactly as it is, and setting it
 * active again reopens it.
 */
export const stopServer = (serverId: number, refundPlayers = false) =>
    send(`/server/${serverId}/stop`, 'POST', { refund_players: refundPlayers })

/** Destroy the machine and rebuild it in the same slot. Minutes, not seconds. */
export const hardRestartServer = (serverId: number, refundPlayers = false, machineType?: string) =>
    send(`/server/${serverId}/hard-restart`, 'POST', {
        refund_players: refundPlayers,
        ...(machineType && machineType !== 'default' ? { machine_type: machineType } : {}),
    })

/**
 * Ask a server to disconnect a player.
 *
 * Returns 501 while kicking is switched off, which is deliberate — a button
 * that silently does nothing is worse than no button. Needs a game build that
 * polls sync_online_players and acts on the kick list.
 */
export const kickPlayer = (serverId: number, userIds: number[]) =>
    send(`/server/${serverId}/kick`, 'POST', { user_ids: userIds })

export type BanRequest = {
    user_id: number
    reason: number
    is_permanent: boolean
    /** Absolute end time; omit for a permanent ban. */
    banned_until?: string | null
    comment?: string | null
    /** 1 = Pause (investigating, reversible), 2 = Ban (they cheated).
     *  Omitted means Ban, which is what every caller written before this
     *  field meant. */
    kind?: 1 | 2
}

// The ban tool sits outside /team-tools/api, so it does not go through `send`.
export const banPlayer = async (request: BanRequest): Promise<{ ban_id: number }> => {
    const response = await fetch('/team-tools/api/ban-tool/user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: JSON.stringify(request),
    })
    if (!response.ok) {
        // The view returns plain text on a bad request, not JSON.
        const detail = await response.text().catch(() => '')
        throw new Error(detail || `Ban failed (${response.status})`)
    }
    return response.json()
}

// Partial edit. Only the keys sent are applied, so two people editing different
// fields of the same server do not overwrite each other.
export const updateServer = (serverId: number, data: Record<string, any>) =>
    send(`/server/${serverId}/update`, 'POST', data)

export const getServerHealth = (serverId: number): Promise<any> => send(`/server/${serverId}/health`, 'GET')

// --- Cheat review queue ---------------------------------------------------
//
// Reads are staff, acting is developer, matching everything else here. The two
// actions are gated differently on purpose: `banCheatReport` acts at once,
// while `revokeCheatPayout` only *proposes* — taking money back is the one
// thing on that screen that goes through the approval queue.

/**
 * One detector's finding, said for somebody who does not read server logs.
 *
 * `title` is what the player did. `explanation` is why the game thinks so.
 * `caveat` is only set when the detector is known to be misfiring right now,
 * and it is the reason a reviewer should not treat that row as evidence.
 */
export type ExplainedSignal = {
    name: string
    title: string
    explanation: string
    caveat: string
    /** True when this signal was allowed to end the player's match. */
    enforcing: boolean
    count: number
    weight: number
    /** The game server's own sentence, verbatim. Shown under the toggle. */
    detail: string
    known: boolean
}

/**
 * What actually happened to the player.
 *
 * `unknown` is a real answer, not a hedge. Reports from server builds before
 * the enforcement header simply do not say, and calling those "reported only"
 * would be a guess in the flattering direction.
 */
export type Outcome = 'kicked' | 'reported' | 'unknown'

export type ExplainedEvidence = {
    outcome: Outcome
    outcome_label: string
    outcome_note: string
    headline: string
    parsed: boolean
    truncated: boolean
    score: number | null
    threshold: number | null
    enforcement_score: number | null
    kick_threshold: number | null
    caveats: string[]
    signals: ExplainedSignal[]
}

export type CheatReport = {
    id: number
    state: number
    state_label: string
    created_at: string
    user_id: number | null
    username: string | null
    /** The server could not tie this flag to an account at all. */
    unresolved_user: boolean
    session_id: string | null
    server_id: number
    server_name: string | null
    connection_id: number
    score: number
    threshold: number
    /** Verbatim, as the server sent it. Behind the technical toggle. */
    evidence: string
    /** The same string, read. This is what the card renders. */
    explained: ExplainedEvidence
    reviewed_by: string | null
    reviewed_at: string | null
    user_is_banned: boolean
    // Staff-only, and only meaningful when `user_is_banned`. `banned_by`
    // is null for an automatic ban, which is an answer rather than
    // missing data -- `banTooltip` names it.
    banned_by?: string | null
    ban_reason?: string | null
    ban_comment?: string | null
    revoke_pending: boolean
}

export const getCheatReports = (state = 'open'): Promise<{ reports: CheatReport[] }> =>
    send(`/cheat-reports?state=${encodeURIComponent(state)}`, 'GET')

export const setCheatReportState = (id: number, state: string) => send(`/cheat-reports/${id}/state`, 'POST', { state })

/** Raises a StaffActionRequest. Nothing is taken back until it is approved. */
export const revokeCheatPayout = (id: number, reason?: string) =>
    send(`/cheat-reports/${id}/revoke`, 'POST', { ...(reason ? { reason } : {}) })

/** Stops the account and queues a kick from whatever match they are in.
 *
 *  `kind` picks which of the two states: a pause says "we are looking at this
 *  and you are expected back", a ban says "you cheated". Both stop the account
 *  identically; the difference is what the player is told. */
export const banCheatReport = (id: number, playerMessage?: string, kind: 1 | 2 = 2) =>
    send(`/cheat-reports/${id}/ban`, 'POST', {
        kind,
        ...(playerMessage ? { player_message: playerMessage } : {}),
    })

/**
 * Ends the match this player is in, and nothing else.
 *
 * Separate from ban rather than an option on it, because they are different
 * sizes of decision: a kick costs a session, a ban costs days and every server.
 * Before this, the only button that removed somebody also banned them, so
 * "get them out of there" and "I have concluded they are a cheater" were the
 * same click.
 */
export const kickCheatReport = (id: number) => send(`/cheat-reports/${id}/kick`, 'POST', {})

// --- Automatic enforcement -------------------------------------------------
//
// Read-only here. The policy is configured in Django admin because it bans
// people and that should be the smaller set of hands; it is shown on this
// screen because a reviewer who cannot see that an account is one report away
// from an automatic ban is reviewing without the fact that changes what they
// should do.

export type EnforcementPolicy = {
    mode: number
    mode_label: string
    runs: boolean
    acts: boolean
    reports_before_ban: number
    window_days: number
    /** 1 every detector's reports together, 2 each detector separately. */
    count_scope: number
    count_scope_label: string
    enforced_reports_only: boolean
    ban_days: number
    ban_hours: number
    /** The first automatic ban's length after the max_ban_days cap. */
    first_ban_days: number
    first_ban_hours: number
    escalate: boolean
    max_ban_days: number
    freeze_funds: boolean
    refund_victims: boolean
    refund_window_hours: number
    max_refunds_per_ban: number
    player_message: string
    configured: boolean
}

export type EnforcementSignal = {
    signal: string
    action: number
    action_label: string
    reports_before_ban: number | null
    /** The count that bans on this detector: its override, or the policy's. */
    required: number
    note: string
}

export type EnforcementAction = {
    id: number
    created_at: string
    user_id: number | null
    username: string | null
    report_id: number | null
    outcome: number
    outcome_label: string
    /** The field the whole screen turns on: false means it was a prediction. */
    applied: boolean
    signal: string
    count: number
    required: number
    ban_id: number | null
    ban_days: number
    ban_hours: number
    /** Whether the money is held. False on a ban is the row that needs a person. */
    funds_frozen: boolean
    freeze_note: string
    /** Victims found with NO request raised is the row that needs a person. */
    victims_found: number
    /** A request id means somebody was ASKED to pay, not that anybody was paid. */
    refund_request_id: number | null
    refund_note: string
    explanation: string
}

export type EnforcementResponse = {
    policy: EnforcementPolicy
    signals: EnforcementSignal[]
    actions: EnforcementAction[]
    summary: {
        banned: number
        would_have_banned: number
        kicked: number
        counting: number
        banned_funds_not_held: number
        victims_owed_unrequested: number
    }
    inert_because: string | null
}

export const getCheatEnforcement = (): Promise<EnforcementResponse> => send('/cheat-enforcement', 'GET')

// --- Integrity ranking -----------------------------------------------------
//
// A sibling of the cheat queue, not part of it. The queue lists players a
// detector flagged; this lists players whose telemetry sits furthest from the
// population, and no detector fired on most of them. A score is a place to
// start looking, never a finding — so nothing here writes a cheat report, and
// `revokeIntegrityPayout` only *proposes*, exactly as it does on the queue.

export type IntegrityComponents = {
    aim: number
    smooth: number
    ammo: number
    damage: number
    /**
     * Damage-per-second cap breaches. Scored from v2 on, and weighted above
     * `damage` because the rate check reads neither weaponMax nor bodyPart, so
     * unlike the ceiling check it cannot inherit either resolution bug.
     * Always 0 on a score computed under v1, which had no column for it.
     */
    rate: number
    flag: number
}

export type IntegrityReadings = {
    max_headshot_pct: number | null
    hits_at_max: number | null
    divergence_mean_at_max: number | null
    over30_pct_at_max: number | null
    cam_offset_at_max: number | null
    /** Connections past the population p99 with no wide correction at all. */
    smooth_outlier_sessions: number
    peak_shots_in_magazine: number | null
    max_total_shots: number | null
    damage_events_trusted: number
    /** Guard events computed from a weaponMax we know is wrong. Never scored. */
    damage_events_untrusted: number
    /** Rate-cap breaches. Trustworthy, and scored, unlike the two above. */
    damage_rate_events: number
    /** Worst second as a multiple of the cap. Shown, never scored. */
    damage_rate_max_ratio: number | null
    watch_count: number
    flagged_count: number
    telemetry_rows: number
    servers: number[]
}

export type TelemetryBaseline = {
    headshot_pct_p99: number
    divergence_mean_median: number
    sample_count: number
    min_hits?: number
    computed_at: string
}

export type IntegrityScore = {
    user_id: number
    username: string | null
    score: number
    components: IntegrityComponents
    readings: IntegrityReadings
    /** Which weights produced this score. They are expected to change. */
    weights_version: string
    computed_at: string
    window_start: string
    window_end: string
    baseline: TelemetryBaseline | null
    user_is_banned: boolean
    // Staff-only, and only meaningful when `user_is_banned`. `banned_by`
    // is null for an automatic ban, which is an answer rather than
    // missing data -- `banTooltip` names it.
    banned_by?: string | null
    ban_reason?: string | null
    ban_comment?: string | null
    /** Whether a detector ever fired on this player. Usually false. */
    has_cheat_report: boolean
}

export type TelemetryRow = {
    id: number
    measured_at: string
    server_id: number
    server_name: string | null
    connection_id: number
    session_id: string | null
    source: number
    source_label: string
    build_version: string
    aim: {
        hits: number | null
        headshots: number | null
        headshot_pct: number | null
        divergence_mean: number | null
        divergence_max: number | null
        over30_count: number | null
        over30_pct: number | null
        cam_offset_violations: number | null
    }
    ammo: {
        total_shots: number | null
        reloads: number | null
        peak_shots_in_magazine: number | null
        shot_budget: number | null
    }
    damage: {
        events: number
        events_untrusted: number
        rate_events: number
        rate_max_ratio: number | null
        max: number | null
        max_overshoot_ratio: number | null
    }
    // Game cs:15953 on; every field is null on older rows. combat_dps is null, not 0, when
    // the session landed no enemy hit. Display only: nothing scores these yet.
    combat: {
        damage: number | null
        seconds: number | null
        gap_seconds: number | null
        rate_peak: number | null
        rate_window_seconds: number | null
        combat_dps: number | null
        peak_dps: number | null
    }
    detectors: {
        max_cheat_score: number | null
        watch_count: number
        was_flagged: boolean
        signals: Record<string, number>
    }
}

export const getIntegrityScores = (
    minScore?: number
): Promise<{
    scores: IntegrityScore[]
    weights_version: string
    baseline: TelemetryBaseline | null
}> => send(`/integrity/players${minScore ? `?min_score=${encodeURIComponent(minScore)}` : ''}`, 'GET')

/** The rows behind one score. A summary is not evidence. */
export const getPlayerTelemetry = (
    userId: number
): Promise<{ user_id: number; score: IntegrityScore | null; telemetry: TelemetryRow[] }> =>
    send(`/integrity/players/${userId}/telemetry`, 'GET')

/** Telemetry rows that already have a revoke sitting in the approval queue. */
// Sessions, not telemetry rows: one session produces several rows, and the dedupe key the
// backend writes is per session. See IntegrityPendingRevokesView.
export const getPendingIntegrityRevokes = (): Promise<{ session_ids: string[] }> =>
    send('/integrity/pending-revokes', 'GET')

/**
 * Raises a StaffActionRequest against ONE session, not against the player.
 * Nothing is taken back until somebody else approves it.
 */
export const revokeIntegrityPayout = (telemetryId: number, reason?: string) =>
    send(`/integrity/telemetry/${telemetryId}/revoke`, 'POST', {
        ...(reason ? { reason } : {}),
    })

/** Stops the account and queues a kick. Same code path as the cheat queue's.
 *
 *  Pause matters more here than anywhere: this screen acts on a RANKING of
 *  statistical outliers, which is explicitly not a finding. */
export const banIntegrityPlayer = (userId: number, playerMessage?: string, kind: 1 | 2 = 2) =>
    send(`/integrity/players/${userId}/ban`, 'POST', {
        kind,
        ...(playerMessage ? { player_message: playerMessage } : {}),
    })
